import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Price + time estimates — shown live when user selects device + issue
const priceMatrix = {
  iPhone: {
    "Screen Repair": { min: 49, max: 329, time: "30–60 min" },
    "Battery Replacement": { min: 39, max: 149, time: "15–30 min" },
    "Charging Port Repair": { min: 49, max: 99, time: "45–90 min" },
    "Water Damage": { min: 59, max: 199, time: "24–48 hrs" },
    "Data Recovery": { min: 69, max: 299, time: "Same day–2 days" },
    "Software Issue": { min: 39, max: 99, time: "30–60 min" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
  Samsung: {
    "Screen Repair": { min: 59, max: 349, time: "45–90 min" },
    "Battery Replacement": { min: 49, max: 129, time: "30–45 min" },
    "Charging Port Repair": { min: 59, max: 119, time: "45–90 min" },
    "Water Damage": { min: 59, max: 249, time: "24–48 hrs" },
    "Data Recovery": { min: 69, max: 299, time: "Same day–2 days" },
    "Software Issue": { min: 39, max: 99, time: "30–60 min" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
  iPad: {
    "Screen Repair": { min: 79, max: 399, time: "1–3 hrs" },
    "Battery Replacement": { min: 69, max: 179, time: "1–2 hrs" },
    "Charging Port Repair": { min: 69, max: 149, time: "1–2 hrs" },
    "Water Damage": { min: 79, max: 299, time: "24–48 hrs" },
    "Data Recovery": { min: 79, max: 299, time: "Same day–2 days" },
    "Software Issue": { min: 49, max: 119, time: "30–60 min" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
  MacBook: {
    "Screen Repair": { min: 349, max: 699, time: "1–3 days" },
    "Battery Replacement": { min: 149, max: 249, time: "Same day" },
    "Charging Port Repair": { min: 149, max: 249, time: "Same day" },
    "Water Damage": { min: 199, max: 599, time: "2–5 days" },
    "Data Recovery": { min: 99, max: 299, time: "1–2 days" },
    "Software Issue": { min: 79, max: 149, time: "Same day" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
  Laptop: {
    "Screen Repair": { min: 149, max: 299, time: "1–3 days" },
    "Battery Replacement": { min: 99, max: 179, time: "Same day" },
    "Charging Port Repair": { min: 99, max: 179, time: "1–2 days" },
    "Water Damage": { min: 149, max: 449, time: "2–5 days" },
    "Data Recovery": { min: 99, max: 299, time: "1–2 days" },
    "Software Issue": { min: 79, max: 149, time: "Same day" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
  Other: {
    "Screen Repair": { min: 59, max: 249, time: "Same day" },
    "Battery Replacement": { min: 39, max: 149, time: "Same day" },
    "Charging Port Repair": { min: 49, max: 149, time: "Same day" },
    "Water Damage": { min: 59, max: 249, time: "24–48 hrs" },
    "Data Recovery": { min: 69, max: 299, time: "1–2 days" },
    "Software Issue": { min: 39, max: 99, time: "Same day" },
    "Quote Request": { min: 0, max: 0, time: "Free diagnostic" },
  },
};

const DEVICE_OPTIONS = [
  { value: "iPhone", label: "iPhone" },
  { value: "Samsung", label: "Samsung" },
  { value: "iPad", label: "iPad / Tablet" },
  { value: "MacBook", label: "MacBook" },
  { value: "Laptop", label: "Windows Laptop" },
  { value: "Other", label: "Other" },
];

const ISSUE_OPTIONS = [
  { value: "Screen Repair", label: "Cracked / Broken Screen" },
  { value: "Battery Replacement", label: "Battery Issue" },
  { value: "Charging Port Repair", label: "Won't Charge" },
  { value: "Water Damage", label: "Water Damage" },
  { value: "Data Recovery", label: "Lost / Need Data" },
  { value: "Software Issue", label: "Software / Slow" },
  { value: "Quote Request", label: "Something Else" },
];

// Custom dropdown — avoids the native <select> rendering issues on this site
const CustomDropdown = ({ value, onChange, placeholder, options, label, id }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <label
        htmlFor={id}
        style={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "#333",
          marginBottom: "5px",
          display: "block",
        }}
      >
        {label}
      </label>
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: "100%",
          padding: "11px 36px 11px 14px",
          borderRadius: "8px",
          border: "1px solid #ddd",
          fontSize: "0.95rem",
          color: selected ? "#18305d" : "#999",
          fontWeight: selected ? 600 : 400,
          background: "#ffffff",
          textAlign: "left",
          cursor: "pointer",
          lineHeight: 1.2,
          position: "relative",
          outline: "none",
        }}
      >
        {selected ? selected.label : placeholder}
        <i
          className={`fas fa-chevron-${open ? "up" : "down"}`}
          style={{
            position: "absolute",
            right: "14px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#18305d",
            fontSize: "0.8rem",
            pointerEvents: "none",
          }}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#ffffff",
            border: "1px solid #ddd",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            listStyle: "none",
            margin: 0,
            padding: "6px 0",
            maxHeight: "260px",
            overflowY: "auto",
            zIndex: 50,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              style={{
                padding: "10px 14px",
                fontSize: "0.92rem",
                color: value === opt.value ? "#F50963" : "#18305d",
                background: value === opt.value ? "#fff5f8" : "transparent",
                fontWeight: value === opt.value ? 700 : 500,
                cursor: "pointer",
                transition: "background 0.12s ease",
              }}
              onMouseEnter={(e) => {
                if (value !== opt.value) e.currentTarget.style.background = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                if (value !== opt.value) e.currentTarget.style.background = "transparent";
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const InstantQuoteWidget = () => {
  const [device, setDevice] = useState("");
  const [issue, setIssue] = useState("");
  const navigate = useNavigate();

  // Live estimate preview
  const estimate =
    device && issue && priceMatrix[device] && priceMatrix[device][issue]
      ? priceMatrix[device][issue]
      : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (device) params.set("device", device);
    if (issue) params.set("service", issue);
    navigate(`/contact-us?${params.toString()}#contact-form`);
  };

  return (
    <section
      style={{
        background: "linear-gradient(135deg, #F50963 0%, #c40851 100%)",
        padding: "50px 0",
        color: "#ffffff",
      }}
      aria-label="Get an instant repair quote"
    >
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-5 mb-30 mb-lg-0">
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(255,255,255,0.15)",
                  padding: "5px 14px",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  marginBottom: "15px",
                }}
              >
                <i className="fas fa-bolt mr-2" />
                SAME-DAY REPAIR
              </div>
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: 800,
                  lineHeight: 1.2,
                  marginBottom: "10px",
                  color: "#ffffff",
                }}
              >
                Fix My Phone Today — Get an Instant Quote
              </h2>
              <p style={{ fontSize: "1rem", opacity: 0.9, margin: 0 }}>
                Tell us your device and issue. We'll show your price range
                instantly and reply within 1 hour to book your same-day repair.
              </p>
            </div>
          </div>

          <div className="col-lg-7">
            <form
              onSubmit={handleSubmit}
              style={{
                background: "#ffffff",
                borderRadius: "14px",
                padding: "22px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              }}
            >
              <div className="row">
                <div className="col-md-4 mb-15 mb-md-0">
                  <CustomDropdown
                    id="iq-device"
                    label="Your Device"
                    placeholder="Select device…"
                    value={device}
                    onChange={setDevice}
                    options={DEVICE_OPTIONS}
                  />
                </div>

                <div className="col-md-5 mb-15 mb-md-0">
                  <CustomDropdown
                    id="iq-issue"
                    label="What's Wrong?"
                    placeholder="Select issue…"
                    value={issue}
                    onChange={setIssue}
                    options={ISSUE_OPTIONS}
                  />
                </div>

                <div className="col-md-3 d-flex align-items-end">
                  <button
                    type="submit"
                    disabled={!device || !issue}
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      background:
                        "linear-gradient(135deg, #18305d 0%, #0f1d3a 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      cursor: device && issue ? "pointer" : "not-allowed",
                      whiteSpace: "nowrap",
                      opacity: device && issue ? 1 : 0.6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      lineHeight: 1.2,
                      minHeight: "44px",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <span>Fix Today</span>
                    <i className="fas fa-arrow-right" style={{ fontSize: "0.8rem" }} />
                  </button>
                </div>
              </div>

              {/* Live estimate display */}
              {estimate && (
                <div
                  style={{
                    marginTop: "18px",
                    padding: "14px 16px",
                    background:
                      "linear-gradient(135deg, #fff3d1 0%, #ffe5a8 100%)",
                    borderRadius: "10px",
                    border: "2px solid #ffaa19",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#8b6914",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      <i className="fas fa-tag mr-1" />
                      Estimated Price Range
                    </div>
                    <div
                      style={{
                        fontSize: "1.4rem",
                        fontWeight: 800,
                        color: "#18305d",
                        marginTop: "2px",
                      }}
                    >
                      {estimate.min === 0 && estimate.max === 0
                        ? "Free Diagnostic"
                        : `$${estimate.min} – $${estimate.max}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#8b6914",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      <i className="fas fa-clock mr-1" />
                      Ready In
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "#18305d",
                        marginTop: "2px",
                      }}
                    >
                      {estimate.time}
                    </div>
                  </div>
                </div>
              )}
            </form>

            <div
              style={{
                marginTop: "12px",
                fontSize: "0.82rem",
                color: "rgba(255,255,255,0.9)",
                textAlign: "center",
              }}
            >
              <i className="fas fa-shield-alt mr-2" />
              30-Day Warranty · Free Diagnostic · Same-Day Service · 10,000+
              Happy Customers
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstantQuoteWidget;
